import React, { useState, useEffect } from 'react';
import MarketplaceCard from '../components/MarketplaceCard';
import RecentlyViewed from '../components/RecentlyViewed';
import { motion } from 'framer-motion';
import { API_URL } from '../config';
import Spinner from '../components/Spinner';


const Marketplace = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filter States
  const [searchTerm, setSearchTerm] = useState('');
  const [platformFilter, setPlatformFilter] = useState('All Platforms');
  const [categoryFilter, setCategoryFilter] = useState('All Strategies');
  const [sortBy, setSortBy] = useState('Top Rated');

  useEffect(() => {
    fetch(`${API_URL}/api/products`)
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) {
          setProducts(data);
        } else {
          console.error("API did not return an array of products:", data);
          setProducts([]);
        }
        setLoading(false);
      })
      .catch(err => {
        console.error('Failed to fetch products', err);
        setLoading(false);
      });
  }, []);

  const filteredProducts = products.filter(product => {
    // Platform
    if (platformFilter !== 'All Platforms' && product.platform !== platformFilter) return false;
    
    // Category
    if (categoryFilter !== 'All Strategies' && product.category !== categoryFilter) return false;

    // Search
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      const matchName = product.name?.toLowerCase().includes(term);
      const matchTag = product.tagline?.toLowerCase().includes(term);
      if (!matchName && !matchTag) return false;
    }
    return true;
  });

  const sortedProducts = [...filteredProducts].sort((a, b) => {
    // Priority sorting: If a product's badge exactly matches the selected Sort By option, pin it to the top
    if (a.badge === sortBy && b.badge !== sortBy) return -1;
    if (b.badge === sortBy && a.badge !== sortBy) return 1;

    // Standard sorting algorithm
    if (sortBy === 'Price: Low to High') {
      return (parseFloat(a.newPrice) || 0) - (parseFloat(b.newPrice) || 0);
    }
    if (sortBy === 'Lowest Drawdown') {
      return (parseFloat(a.maxDrawdown) || 0) - (parseFloat(b.maxDrawdown) || 0);
    }
    if (sortBy === 'Highest ROI') {
      return (parseFloat(b.winRate) || 0) - (parseFloat(a.winRate) || 0);
    }
    return 0; // default fallback
  });

  return (
    <main className="min-h-screen relative">
      {/* Ambient background glow */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-primary/5 rounded-full blur-[120px] -z-10 pointer-events-none"></div>
      <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-secondary/5 rounded-full blur-[150px] -z-10 pointer-events-none"></div>
      
      <section className="max-w-container-max mx-auto px-margin-mobile md:px-margin-desktop py-12">
        {/* Header Section */}
        <div className="mb-12">
          <h1 className="font-display-lg text-display-lg text-silver-text mb-4">
            Explore Our <span className="text-primary">Elite EA Marketplace</span>
          </h1>
          <p className="font-body-lg text-body-lg text-on-surface-variant max-w-2xl">
            High-performance algorithmic trading tools curated for institutional-grade results. Precision engineered, backtested for years, and optimized for maximum yield.
          </p>
        </div>

        {/* Filter & Search Bar Cluster */}
        <div className="glass-card p-6 rounded-xl mb-12 flex flex-wrap items-end gap-gutter-desktop">
          {/* Search */}
          <div className="flex-1 min-w-[280px]">
            <label className="block font-label-sm text-label-sm text-on-surface-variant mb-2">Search Marketplace</label>
            <div className="relative">
              <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-on-surface-variant">search</span>
              <input 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full bg-surface-container-low border border-white/10 rounded-lg py-3 pl-10 pr-4 focus:ring-1 focus:ring-primary focus:border-primary text-silver-text outline-none transition-all" 
                placeholder="Search by name, strategy, or author..." 
                type="text" 
              />
            </div>
          </div>
          
          {/* Platform Filter */}
          <div className="w-full md:w-auto min-w-[160px]">
            <label className="block font-label-sm text-label-sm text-on-surface-variant mb-2">Platform</label>
            <select 
              value={platformFilter}
              onChange={(e) => setPlatformFilter(e.target.value)}
              className="w-full bg-surface-container-low border border-white/10 rounded-lg py-3 px-4 text-silver-text outline-none focus:ring-1 focus:ring-primary"
            >
              <option>All Platforms</option>
              <option>MetaTrader 4 (MT4)</option>
              <option>MetaTrader 5 (MT5)</option>
            </select>
          </div>
          
          {/* Category Filter */}
          <div className="w-full md:w-auto min-w-[160px]">
            <label className="block font-label-sm text-label-sm text-on-surface-variant mb-2">Category</label>
            <select 
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="w-full bg-surface-container-low border border-white/10 rounded-lg py-3 px-4 text-silver-text outline-none focus:ring-1 focus:ring-primary"
            >
              <option>All Strategies</option>
              <option>Scalper</option>
              <option>Grid / Martingale</option>
              <option>Trend Following</option>
              <option>Arbitrage</option>
              <option>News Trading</option>
            </select>
          </div>
          
          {/* Sort */}
          <div className="w-full md:w-auto min-w-[160px]">
            <label className="block font-label-sm text-label-sm text-on-surface-variant mb-2">Sort By</label>
            <select 
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="w-full bg-surface-container-low border border-white/10 rounded-lg py-3 px-4 text-silver-text outline-none focus:ring-1 focus:ring-primary"
            >
              <option>Top Rated</option>
              <option>Lowest Drawdown</option>
              <option>Highest ROI</option>
              <option>Newest Arrivals</option>
              <option>Price: Low to High</option>
            </select>
          </div>
        </div>

        {/* Product Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-gutter-desktop">
          {loading ? (
            <Spinner text="Loading Expert Advisors..." />
          ) : (
            <>
              {sortedProducts.map(product => (
                <MarketplaceCard key={product.id} product={product} />
              ))}
              {sortedProducts.length === 0 && (
                <div className="col-span-full text-center py-12 text-on-surface-variant bg-surface-container-low rounded-xl border border-white/5">
                  <span className="material-symbols-outlined text-4xl mb-3 text-white/20">search_off</span>
                  <p className="font-bold text-silver-text">No Expert Advisors found.</p>
                  <p className="text-sm mt-1">Try adjusting your filters or search term.</p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Custom Request Banner */}
        <div className="mt-20 p-8 rounded-2xl glass-card relative overflow-hidden flex flex-col md:flex-row items-center justify-between gap-8 border border-white/5">
          <div className="absolute inset-0 bg-primary/5 -z-10 blur-3xl opacity-30"></div>
          <div className="max-w-xl text-center md:text-left">
            <h2 className="font-headline-md text-headline-md text-silver-text mb-4">Don't See the EA You Need? <span className="text-primary">We'll Get It For You.</span></h2>
            <p className="font-body-md text-body-md text-on-surface-variant">
              Send us the MQL5 link of any Expert Advisor and we'll source it and list it at our discounted price—typically within 24 hours. No hidden fees, just savings.
            </p>
          </div>
          <button className="whitespace-nowrap bg-primary-container text-on-primary-container font-bold px-10 py-4 rounded-xl flex items-center gap-3 hover:scale-105 transition-all shadow-xl shadow-primary/10">
            Request an EA
            <span className="material-symbols-outlined">arrow_forward</span>
          </button>
        </div>
      </section>

      {/* Recently Viewed Section */}
      <RecentlyViewed />

      {/* Trust Signals Section */}
      <section className="bg-surface-container-lowest/50 py-20 border-y border-white/5">
        <div className="max-w-container-max mx-auto px-margin-mobile md:px-margin-desktop">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-gutter-desktop">
            <div className="flex gap-6 items-start">
              <div className="bg-primary/10 p-4 rounded-full">
                <span className="material-symbols-outlined text-primary text-[32px]">verified_user</span>
              </div>
              <div>
                <h4 className="font-headline-sm text-headline-sm text-silver-text mb-2">Verified Backtests</h4>
                <p className="font-body-md text-body-md text-on-surface-variant">All EAs come with real-time trading logs and verifiable strategy tester reports.</p>
              </div>
            </div>
            <div className="flex gap-6 items-start">
              <div className="bg-secondary/10 p-4 rounded-full">
                <span className="material-symbols-outlined text-secondary text-[32px]">payments</span>
              </div>
              <div>
                <h4 className="font-headline-sm text-headline-sm text-silver-text mb-2">80-90% Discount</h4>
                <p className="font-body-md text-body-md text-on-surface-variant">Access premium institutional tools at a fraction of their marketplace price.</p>
              </div>
            </div>
            <div className="flex gap-6 items-start">
              <div className="bg-tertiary/10 p-4 rounded-full">
                <span className="material-symbols-outlined text-tertiary text-[32px]">support_agent</span>
              </div>
              <div>
                <h4 className="font-headline-sm text-headline-sm text-silver-text mb-2">Expert Support</h4>
                <p className="font-body-md text-body-md text-on-surface-variant">Technical assistance with installation, optimization, and VPS setup.</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Marketplace;
